import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import util from '../utils/util';
import moment from 'moment';

// ---------------------------------------------------------------------------
// Registry: every routable module, with a short purpose line for search +
// scanability. Purpose lines follow the "name what it does, not how it's
// built" rule — plain, active, specific.
// ---------------------------------------------------------------------------
const PAGE_REGISTRY = [
    { key:'manage_roles_users',   label:'Roles & Users',        icon:'👤', path:'/roles',                        desc:'Manage accounts, roles and permissions' },
    { key:'manage_facilities',    label:'Facilities',           icon:'🏥', path:'/facilities',                   desc:'Maintain the national facility master list' },
    { key:'qoc_facility_coords',  label:'Facility QR Codes',    icon:'📍', path:'/qoc/facility-coordinates',     desc:'Generate and print facility QR codes' },
    { key:'qoc_user_manual',      label:'User Manual',          icon:'📖', path:'/qoc/user-manual',              desc:'Platform documentation and reference guide' },
    { key:'qoc_standards',        label:'Standards Setup',      icon:'📋', path:'/qoc/standards',                desc:'Configure quality-of-care standards and criteria' },
    { key:'qoc_approve',          label:'Approval Queue',       icon:'☑',  path:'/qoc/approval',                 desc:'Review and approve submitted assessments' },
    { key:'qoc_assess',           label:'Assessments',          icon:'✅', path:'/qoc/assessments',              desc:'Conduct and manage facility assessments' },
    { key:'qoc_import',           label:'Import (KoBo)',        icon:'📥', path:'/qoc/assessment-import',        desc:'Import assessment data collected via KoBoCollect' },
    { key:'qoc_cap',              label:'Corrective Actions',   icon:'🔧', path:'/qoc/cap',                      desc:'Track corrective action plans and follow-up' },
    { key:'qoc_team',             label:'Audit Team',           icon:'👥', path:'/qoc/team-registry',            desc:'Maintain the auditor and assessment team registry' },
    { key:'qoc_facility_profile', label:'Facility Profile',     icon:'🏢', path:'/qoc/facility-profile',         desc:'View detailed facility profile information' },
    { key:'ays_submit',           label:'AYS Scorecard',        icon:'📋', path:'/qoc/ays/scorecard',            desc:'Submit Adolescent & Youth Services scorecards' },
    { key:'ays_admin',            label:'AYS Management',       icon:'⚙',  path:'/qoc/ays/admin',                desc:'Configure AYS scorecard settings' },
    { key:'ays_analytics',        label:'AYS Analytics',        icon:'📊', path:'/qoc/ays/analytics',            desc:'Analyse Adolescent & Youth Services trends' },
    { key:'qoc_nominations',      label:'Nominations',          icon:'🎖️', path:'/qoc/nominations',              desc:'Submit nominations for health awards' },
    { key:'prog_assess',          label:'Programs Assessment',  icon:'📂', path:'/qoc/programs',                 desc:'Assess programs for award consideration' },
    { key:'ha_judge',             label:'Judge Portal',         icon:'⚖️', path:'/qoc/judge-portal',             desc:'Score and review award nominations' },
    { key:'prog_admin',           label:'Awards Dashboard',     icon:'🏆', path:'/qoc/awards',                   desc:'Track health awards programme progress' },
    { key:'survey_admin',         label:'Survey Builder',       icon:'🔨', path:'/qoc/survey-builder',           desc:'Design and publish custom surveys' },
    { key:'survey_collect',       label:'My Surveys',           icon:'📝', path:'/qoc/my-surveys',               desc:'Complete surveys assigned to you' },
    { key:'qoc_analytics',        label:'Analytics',            icon:'📊', path:'/qoc/analytics',                desc:'National-level quality analytics' },
    { key:'qoc_compliance',       label:'Compliance',           icon:'🛡', path:'/qoc/compliance',               desc:'Monitor facility compliance status' },
    { key:'qoc_kpis',             label:'KPI Targets',          icon:'🎯', path:'/qoc/kpis',                     desc:'Set and track key performance indicators' },
    { key:'qoc_survey_dash',      label:'Customer Surveys',     icon:'📝', path:'/qoc/surveys',                  desc:'Review client satisfaction survey results' },
    { key:'qoc_quality_index',    label:'Quality Index',        icon:'⭐', path:'/qoc/quality-index',            desc:'View composite facility quality scores' },
    { key:'qoc_executive_dash',   label:'Executive Dashboard',  icon:'📈', path:'/qoc/executive-dashboard',      desc:'High-level summary view for leadership' },
    { key:'qoc_facility_report',  label:'Facility Reports',     icon:'📄', path:'/qoc/facility-report',          desc:'Generate facility-level reports' },
    { key:'qoc_periods',          label:'Assessment Periods',   icon:'🗓', path:'/qoc/periods',                  desc:'Configure assessment reporting periods' },
    { key:'qoc_schedules',        label:'Audit Schedules',      icon:'📅', path:'/qoc/schedules',                desc:'Plan and schedule facility audits' },
    { key:'qoc_access_control',   label:'Access Control',       icon:'🔐', path:'/qoc/access-control',           desc:'Configure module-level access permissions' },
    { key:'qoc_hie_sync',         label:'HIE Facility Sync',    icon:'🔗', path:'/qoc/hie-sync',                 desc:'Sync facility records with the HIE' },
    { key:'qoc_activity_trail',   label:'Activity Trail',       icon:'🔍', path:'/qoc/activity-trail',           desc:'View system audit and activity logs' },
];

// Each section gets its own accent — the way Salesforce clouds or Microsoft
// 365 app tiles are color-keyed, so 30+ modules still read as distinct
// domains rather than one undifferentiated list.
const SECTIONS = [
    { label:'Administration',     keys:['manage_roles_users','manage_facilities','qoc_facility_coords','qoc_user_manual'], color:'#475569' },
    { label:'Quality of Care',    keys:['qoc_standards','qoc_approve','qoc_assess','qoc_import','qoc_cap','qoc_team','qoc_facility_profile'], color:'#0369a1' },
    { label:'AYS Scorecard',      keys:['ays_submit','ays_admin','ays_analytics'], color:'#7c3aed' },
    { label:'Health Awards',      keys:['qoc_nominations','prog_assess','ha_judge','prog_admin'], color:'#b45309' },
    { label:'National Oversight', keys:['qoc_analytics','qoc_compliance','qoc_kpis','qoc_survey_dash','qoc_quality_index'], color:'#15803d' },
    { label:'Reports',            keys:['qoc_executive_dash','qoc_facility_report'], color:'#4338ca' },
    { label:'Survey Builder',     keys:['survey_admin','survey_collect'], color:'#be185d' },
    { label:'Settings',           keys:['qoc_periods','qoc_schedules','qoc_access_control','qoc_hie_sync','qoc_activity_trail'], color:'#334155' },
];

const FAV_KEY = 'qoc.welcome.favorites';
const RECENT_KEY = 'qoc.welcome.recent';
const MAX_RECENT = 6;

function readList(key) {
    try {
        const raw = JSON.parse(localStorage.getItem(key) || '[]');
        return Array.isArray(raw) ? raw : [];
    } catch {
        return [];
    }
}

export default function Welcome() {
    const history      = useHistory();
    const name         = util.getLoggedName() || 'User';
    const modules      = util.getModules() || {};
    const isAdmin      = parseInt(localStorage.getItem('is_admin') || '0');
    const searchRef    = useRef(null);

    const [query, setQuery]         = useState('');
    const [favorites, setFavorites] = useState(() => readList(FAV_KEY));
    const [recent, setRecent]       = useState(() => readList(RECENT_KEY));

    // "/" focuses search, unless the user is already typing somewhere.
    useEffect(() => {
        const onKeyDown = (e) => {
            const tag = (e.target && e.target.tagName) || '';
            if (e.key === '/' && tag !== 'INPUT' && tag !== 'TEXTAREA') {
                e.preventDefault();
                searchRef.current && searchRef.current.focus();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    const greeting = () => {
        const h = moment().hour();
        if (h < 12) return 'Good morning';
        if (h < 17) return 'Good afternoon';
        return 'Good evening';
    };

    const accessible = useMemo(() => PAGE_REGISTRY.filter((p, i, arr) =>
        (isAdmin || modules[p.key] === 1) &&
        arr.findIndex(x => x.path === p.path) === i
    ), [isAdmin, modules]);

    const accessibleByKey = useMemo(() => {
        const map = {};
        accessible.forEach(p => { map[p.key] = p; });
        return map;
    }, [accessible]);

    const toggleFavorite = (key) => {
        const next = favorites.includes(key) ? favorites.filter(k => k !== key) : [...favorites, key];
        setFavorites(next);
        localStorage.setItem(FAV_KEY, JSON.stringify(next));
    };

    const go = (page) => {
        const next = [page.key, ...recent.filter(k => k !== page.key)].slice(0, MAX_RECENT);
        setRecent(next);
        localStorage.setItem(RECENT_KEY, JSON.stringify(next));
        history.push(page.path);
    };

    const q = query.trim().toLowerCase();
    const matches = (p) => !q || p.label.toLowerCase().includes(q) || p.desc.toLowerCase().includes(q);

    const grouped = useMemo(() => SECTIONS.map(s => ({
        ...s,
        pages: accessible.filter(p => s.keys.includes(p.key) && matches(p)),
    })).filter(s => s.pages.length > 0), [accessible, q]);

    const totalCount   = accessible.length;
    const visibleCount = grouped.reduce((n, s) => n + s.pages.length, 0);

    const quickAccess = useMemo(() => {
        const favSet = new Set(favorites);
        const favPages = favorites.map(k => accessibleByKey[k]).filter(Boolean);
        const recentPages = recent
            .filter(k => !favSet.has(k))
            .map(k => accessibleByKey[k])
            .filter(Boolean);
        return [...favPages, ...recentPages].slice(0, 8);
    }, [favorites, recent, accessibleByKey]);

    const sectionColor = (key) => (SECTIONS.find(s => s.keys.includes(key)) || {}).color || '#334155';

    return (
        <div className="page-content qoc-welcome" style={{ background:'#f1f5f9', minHeight:'100vh' }}>
            <style>{`
                .qoc-welcome * { box-sizing: border-box; }
                .qoc-search { position: relative; flex: 1; max-width: 440px; }
                .qoc-search input {
                    width: 100%; padding: 9px 14px 9px 36px; border-radius: 8px;
                    border: 1px solid rgba(255,255,255,0.15); background: rgba(255,255,255,0.08);
                    color: #fff; font-size: 13px; outline: none; transition: background .15s, border-color .15s;
                }
                .qoc-search input::placeholder { color: rgba(255,255,255,0.45); }
                .qoc-search input:focus { background: rgba(255,255,255,0.16); border-color: #60a5fa; }
                .qoc-search .icon { position:absolute; left:12px; top:50%; transform:translateY(-50%); font-size:13px; opacity:.55; pointer-events:none; }
                .qoc-search .kbd {
                    position:absolute; right:10px; top:50%; transform:translateY(-50%);
                    font-size:10px; color:rgba(255,255,255,0.45); border:1px solid rgba(255,255,255,0.2);
                    border-radius:4px; padding:1px 5px; font-family: ui-monospace, monospace; pointer-events:none;
                }
                .qoc-search input:focus ~ .kbd { display:none; }

                .qoc-chip {
                    display:flex; align-items:center; gap:6px; padding:6px 12px; border-radius:20px;
                    background:#fff; border:1px solid #e2e8f0; font-size:12px; font-weight:600; color:#334155;
                    cursor:pointer; white-space:nowrap; transition: border-color .15s, box-shadow .15s;
                }
                .qoc-chip:hover, .qoc-chip:focus-visible { border-color:#94a3b8; box-shadow:0 1px 3px rgba(15,23,42,0.08); }

                .qoc-card {
                    position:relative; text-align:left; padding:12px 14px; border-radius:10px;
                    background:#fff; border:1px solid #e2e8f0; cursor:pointer;
                    display:flex; align-items:flex-start; gap:10px;
                    transition: border-color .15s, box-shadow .15s, transform .1s;
                }
                .qoc-card:hover { border-color: var(--accent, #94a3b8); box-shadow: 0 4px 14px rgba(15,23,42,0.08); transform: translateY(-1px); }
                .qoc-card:focus-visible { outline: 2px solid #2563eb; outline-offset: 2px; }
                .qoc-card .qoc-star {
                    position:absolute; top:8px; right:8px; opacity:0; border:none; background:transparent;
                    cursor:pointer; font-size:13px; padding:2px; line-height:1; transition: opacity .15s;
                }
                .qoc-card:hover .qoc-star, .qoc-card:focus-within .qoc-star, .qoc-star.is-fav { opacity:1; }

                .qoc-grid { display:grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap:8px; }

                @media (max-width: 640px) {
                    .qoc-header-row { flex-direction: column; align-items: stretch !important; gap:12px; }
                    .qoc-search { max-width: none; }
                }
                @media (prefers-reduced-motion: reduce) {
                    .qoc-card { transition: none; transform: none !important; }
                }
            `}</style>

            {/* Header */}
            <div style={{ background:'linear-gradient(135deg,#0b1f33,#0f2942)', padding:'18px 28px' }}>
                <div className="qoc-header-row" style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:20 }}>
                    <div style={{ flexShrink:0 }}>
                        <div style={{ fontSize:21, fontWeight:800, color:'#fff', letterSpacing:'-0.01em' }}>
                            {greeting()}, <span style={{ color:'#60a5fa' }}>{name.split(' ')[0]}</span>
                        </div>
                        <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)', marginTop:2, fontFamily:'ui-monospace, monospace' }}>
                            {moment().format('dddd, DD MMMM YYYY')}{isAdmin ? ' · Administrator' : ''}
                        </div>
                    </div>

                    <div className="qoc-search">
                        <span className="icon">🔎</span>
                        <input
                            ref={searchRef}
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                            placeholder="Search modules by name or purpose…"
                            aria-label="Search modules"
                        />
                        <span className="kbd">/</span>
                    </div>

                    <div style={{ fontSize:10, color:'rgba(255,255,255,0.25)', textAlign:'right', flexShrink:0 }}>
                        QoC Platform<br/>Eswatini MoH
                    </div>
                </div>
            </div>

            {/* Content */}
            <div style={{ padding:'18px 24px' }}>
                {totalCount === 0 ? (
                    <div style={{ textAlign:'center', padding:'60px 20px', color:'#94a3b8' }}>
                        <div style={{ fontSize:36, marginBottom:12 }}>🔐</div>
                        <div style={{ fontSize:15, fontWeight:700, color:'#334155' }}>No modules assigned yet</div>
                        <div style={{ fontSize:12, marginTop:4 }}>Ask your administrator to grant access to the modules you need.</div>
                    </div>
                ) : (
                    <>
                        {/* Quick access: favorites + recently visited */}
                        {!q && quickAccess.length > 0 && (
                            <div style={{ marginBottom:20 }}>
                                <div style={{ fontSize:10, fontWeight:700, color:'#94a3b8', letterSpacing:1.5, textTransform:'uppercase', marginBottom:8 }}>
                                    Quick access
                                </div>
                                <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                                    {quickAccess.map(p => (
                                        <button key={p.key} className="qoc-chip" onClick={() => go(p)}>
                                            <span>{p.icon}</span>
                                            {p.label}
                                            {favorites.includes(p.key) && <span style={{ color:'#d97706' }}>★</span>}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Search feedback */}
                        {q && (
                            <div style={{ fontSize:12, color:'#64748b', marginBottom:12 }}>
                                Showing <strong style={{ color:'#334155' }}>{visibleCount}</strong> of {totalCount} modules for “{query}”
                            </div>
                        )}

                        {grouped.length === 0 ? (
                            <div style={{ textAlign:'center', padding:'50px 20px', color:'#94a3b8' }}>
                                <div style={{ fontSize:28, marginBottom:10 }}>🗂</div>
                                <div style={{ fontSize:14, fontWeight:700, color:'#334155' }}>No modules match “{query}”</div>
                                <div style={{ fontSize:12, marginTop:4 }}>Try a different term, or clear the search to see everything.</div>
                            </div>
                        ) : grouped.map(section => (
                            <div key={section.label} style={{ marginBottom:22 }}>
                                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                                    <span style={{ width:7, height:7, borderRadius:99, background:section.color, flexShrink:0 }} />
                                    <span style={{ fontSize:11, fontWeight:700, color:'#475569', letterSpacing:1, textTransform:'uppercase' }}>
                                        {section.label}
                                    </span>
                                    <span style={{ fontSize:11, color:'#cbd5e1' }}>{section.pages.length}</span>
                                    <div style={{ height:1, background:'#e2e8f0', flex:1 }} />
                                </div>

                                <div className="qoc-grid">
                                    {section.pages.map(page => (
                                        <div
                                            key={page.key}
                                            className="qoc-card"
                                            style={{ '--accent': section.color }}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => go(page)}
                                            onKeyDown={e => { if (e.key === 'Enter') go(page); }}
                                        >
                                            <div style={{
                                                width:32, height:32, borderRadius:8, background:`${section.color}1a`,
                                                display:'flex', alignItems:'center', justifyContent:'center',
                                                fontSize:15, flexShrink:0,
                                            }}>
                                                {page.icon}
                                            </div>
                                            <div style={{ minWidth:0 }}>
                                                <div style={{ fontSize:13, fontWeight:700, color:'#1e293b' }}>{page.label}</div>
                                                <div style={{ fontSize:11.5, color:'#64748b', marginTop:2, lineHeight:1.35 }}>{page.desc}</div>
                                            </div>
                                            <button
                                                className={`qoc-star ${favorites.includes(page.key) ? 'is-fav' : ''}`}
                                                style={{ color: favorites.includes(page.key) ? '#d97706' : '#94a3b8' }}
                                                onClick={e => { e.stopPropagation(); toggleFavorite(page.key); }}
                                                aria-label={favorites.includes(page.key) ? `Remove ${page.label} from favorites` : `Add ${page.label} to favorites`}
                                                title={favorites.includes(page.key) ? 'Remove from favorites' : 'Add to favorites'}
                                            >
                                                {favorites.includes(page.key) ? '★' : '☆'}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </>
                )}
            </div>
        </div>
    );
}